import { z } from "zod";

/**
 * Le contrat du robot de TikTok (lot M3 de Castellan), partage entre le backend (`packages/backend/src/app/reseaux/robot*.ts`,
 * `atelier/routes.ts`) et la regie. Le robot est un programme du PC de Nicolas que l'agent de l'atelier lance : Castellan ne le
 * joint jamais, c'est l'agent qui appelle Castellan. Le plan est `docs/plans/2026-11-01-castellan-m3.md`.
 */

/**
 * Les trois modes du robot, reglables dans la regie. `arret` (par defaut) : aucune tache, TikTok passe par le brouillon ;
 * `repetition` : le robot remplit tout et s'arrete devant le bouton final, l'envoi revient au repli ; `publication` : le robot
 * clique une fois le bouton final pour l'envoi reserve.
 */
export const MODES_ROBOT = ["arret", "repetition", "publication"] as const;
export const modeRobotSchema = z.enum(MODES_ROBOT);
export type ModeRobot = z.infer<typeof modeRobotSchema>;

/**
 * Les routes de l'agent pour le robot (`/api/atelier/robot/{envoiId}/...`, lot M3). L'agent de l'atelier appelle Castellan :
 * il reserve l'envoi que la tache `programmation` lui a confie, telecharge le fichier, lance le robot du PC, puis rapporte
 * l'issue. Le contrat est recopie des deux cotes (Castellan ici, `packages/atelier` sur le PC).
 */

/** L'issue que le robot rapporte : programme (TikTok Studio l'a), repete (la repetition est allee jusqu'au bout), rien (rien n'est parti), incertain (peut-etre parti). */
export const ISSUES_ROBOT = ["programme", "repete", "rien", "incertain"] as const;
export const issueRobotSchema = z.enum(ISSUES_ROBOT);
export type IssueRobot = z.infer<typeof issueRobotSchema>;

/** Le code du 409 de ces trois routes : l'envoi n'est plus a faire pour cette tache (pris par le repli, deja rapporte, mode a l'arret...). */
export const CODE_ROBOT_SANS_OBJET = "robot_sans_objet";

/**
 * Les causes que le robot rapporte (`resultatRobotSchema.cause`), ecrites par le PC. Chacune a sa phrase dans la regie
 * (`diffusion-texts.ts`, lot suivant).
 */
export const CODES_ROBOT = [
  "robot_controle",
  "robot_chrome_absent",
  "robot_etape_impossible",
  "robot_fichier_invalide",
  "robot_incertain",
  "robot_interrompu",
  "robot_delai_depasse",
] as const;
export type CodeRobot = (typeof CODES_ROBOT)[number];

/** Un identifiant de tache : des chiffres seulement, 18 au plus (tient dans un bigint). */
const identifiantTache = z.string().regex(/^[0-9]{1,18}$/);

/** Le corps de `POST .../reserver` : la tache qui a confie l'envoi au robot. */
export const demandeReservationRobotSchema = z.object({ tacheId: identifiantTache });
export type DemandeReservationRobot = z.infer<typeof demandeReservationRobotSchema>;

/** Ce que l'agent recoit quand l'envoi est reserve : de quoi lancer le robot (le fichier se telecharge ensuite). */
export const reservationRobotSchema = z.object({
  envoiId: z.string(),
  mode: z.enum(["repetition", "publication"]),
  /** ISO 8601 UTC ; le robot le convertit en heure de Paris pour TikTok Studio. */
  moment: z.string(),
  texte: z.string().min(1),
  titre: z.string().nullable(),
  fichier: z.object({
    nom: z.string(),
    taille: z.number().int().positive(),
    empreinte: z.string().regex(/^[0-9a-f]{64}$/),
  }),
});
export type ReservationRobot = z.infer<typeof reservationRobotSchema>;

/** Le corps de `POST .../resultat`. `fenetreJours` et `pasMinutes` sont ce que le robot a mesure de TikTok Studio. */
export const resultatRobotSchema = z.object({
  tacheId: identifiantTache,
  issue: issueRobotSchema,
  cause: z.enum(CODES_ROBOT).optional(),
  fenetreJours: z.number().int().min(0).max(60).optional(),
  pasMinutes: z.number().int().min(1).max(60).optional(),
});
export type ResultatRobot = z.infer<typeof resultatRobotSchema>;

/** La requete de `GET .../fichier` : la tache qui a reserve l'envoi. */
export const requeteFichierRobotSchema = z.object({ tacheId: identifiantTache });
